import axios from 'axios';
import { API_ENDPOINTS } from '../utils/constants';

// This file serves as a proxy for Eventbrite API requests
// It handles the token and manages request formatting properly

interface EventbriteRequestOptions {
  method?: 'get' | 'post' | 'put' | 'delete';
  endpoint: string;
  params?: Record<string, any>;
  token: string;
  data?: any;
}

/**
 * Make a properly formatted request to the Eventbrite API
 * For production use, this logic should be moved to a secure server-side endpoint
 */
export const eventbriteRequest = async ({
  method = 'get',
  endpoint,
  params = {},
  token,
  data
}: EventbriteRequestOptions) => {
  if (!token) {
    throw new Error('No authorization token provided');
  }

  // Implement better error handling with detailed messages
  try {
    // Use our server-side proxy to avoid CORS issues
    let url;
    
    if (endpoint === 'events') {
      url = '/api/eventbrite/events';
    } else if (endpoint.match(/^events\/\d+$/)) {
      const eventId = endpoint.replace('events/', '');
      url = `/api/eventbrite/events/${eventId}`;
    } else {
      throw new Error(`Unsupported endpoint: ${endpoint}`);
    }
    
    console.log(`Making ${method.toUpperCase()} request to Eventbrite API via proxy: ${url}`);
    console.log('Using token:', token.substring(0, 5) + '...');
    console.log('Parameters:', JSON.stringify(params, null, 2));
    
    // Make an API request with proper typing
    const requestConfig: {
      method: string;
      url: string;
      params?: any;
      data?: any;
      headers: Record<string, string>;
    } = {
      method,
      url,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };
    
    // Add params or data based on method
    if (method.toLowerCase() === 'get') {
      requestConfig.params = params;
    } else {
      requestConfig.data = data;
    }
    
    // Make the request
    const response = await axios(requestConfig);
    
    console.log("API response status:", response.status);
    
    return response.data;
  } catch (error: any) {
    console.error('Eventbrite API request failed:', error);
    
    // Format the error message for better debugging
    let errorMessage = 'Failed to fetch data from Eventbrite';
    
    if (error.response) {
      // The API server responded with an error status code
      console.error('Response data:', error.response.data);
      errorMessage = `Eventbrite API Error (${error.response.status}): ${
        error.response.data?.error_description || 
        error.response.data?.error?.message ||
        error.response.data?.error ||
        'Unknown error'
      }`;
      
      if (error.response.status === 401) {
        errorMessage += '. Your API token may be invalid or expired.';
      } else if (error.response.status === 404) {
        // Provide more specific information about 404 errors
        errorMessage = `Eventbrite API Error (404): The path ${error.config?.url || 'requested'} does not exist. Please verify your API endpoint.`;
      }
    } else if (error.request) {
      // The request was made but no response was received
      errorMessage = 'No response received from Eventbrite API. Please check your internet connection.';
    }
    
    throw new Error(errorMessage);
  }
};

// Utility to search events
export const searchEventbriteEvents = async (token: string, searchParams: any) => {
  // Format the search params according to Eventbrite API requirements
  const params: Record<string, any> = {
    expand: 'venue,organizer,ticket_availability'
  };
  
  // Add search query if provided
  if (searchParams.q) {
    params.q = searchParams.q;
  }
  
  // Convert date parameters to the format expected by Eventbrite
  if (searchParams.startDate) {
    const formattedDate = searchParams.startDate instanceof Date 
      ? searchParams.startDate.toISOString().split('T')[0] 
      : searchParams.startDate;
    params['start_date.range_start'] = `${formattedDate}T00:00:00`;
  }
  
  if (searchParams.endDate) {
    const formattedDate = searchParams.endDate instanceof Date 
      ? searchParams.endDate.toISOString().split('T')[0] 
      : searchParams.endDate;
    params['start_date.range_end'] = `${formattedDate}T23:59:59`;
  }
  
  // Add location filter if provided
  if (searchParams.city) {
    params["location.address"] = searchParams.city;
  }
  
  // Add pagination parameters
  params.page = searchParams.page || 1;
  params.page_size = searchParams.pageSize || 10;
  
  // Add online events filter if specified
  if (searchParams.onlineEvents !== undefined) {
    params.online_events = searchParams.onlineEvents;
  }
  
  // Make the actual request through our proxy
  return eventbriteRequest({
    endpoint: 'events/search',
    params,
    token
  });
};

// Utility to get event details
export const getEventbriteEventById = async (token: string, eventId: string) => {
  return eventbriteRequest({
    endpoint: `events/${eventId}`,
    params: {
      expand: 'venue,organizer,ticket_availability'
    },
    token
  });
};
